/**
 * Handles all reveal
 * This is tied to checkout accordion that changes
 * small functionality as the accordion progresses
 * @module reveal
 */

import $ from 'jquery';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import mergeOptions from '../lib/merge-options';

// Expose the function as a jQuery plugin for ease of use
export const PLUGIN_NAME = 'reveal';
export const DEFAULTS = {
  onOpen: () => {},
  onClose: () => {},
  duration: 275,
  toggle: false,
};
/**
 * Return the opening reveal alternative content that toggle opposite from default
 * @param  {jQuery} $el - button /element that opens reveal
 * @return {jQuery} Opening Content
 */
const getAltOpeningContent = ($el) => {
  let content;
  if ($el.attr('data-alt-open')) {
    content = `#${$el.data('alt-open')}`;
  }
  return $(content);
};
/**
 * Return the opening reveal content
 * @param  {jQuery} $el - button /element that opens reveal
 * @return {jQuery} Opening Content
 */
const getOpeningContent = ($el) => {
  let content = `#${$el.data('open')}`;
  if ($el.is('[href]') && $el.attr('href') !== '#') {
    content = $el.attr('href');
  }
  return $(content);
};
/**
 * Close Content
 * @param  {jQuery} $content - reveal content
 * @param {Object} options - Options used for wiring up the reveal
 */
const closeContent = ($content, options = {}) => {
  if ($content) {
    $content
    .slideUp(options.duration)
    .removeClass('reveal-is-active');
  }
};
/**
 * Open Content
 * @param  {jQuery} $content -  reveal content
 * @param {Object} options - Options used for wiring up the reveal
 */
const openContent = ($content, options = {}) => {
  if ($content) {
    $content
    .slideDown(options.duration)
    .addClass('reveal-is-active');
  }
};
/**
 * Toggle Grouped Elements
 * @param {jQuery} $el - button /element that opens reveal
 * @param {Object} options - Options used for wiring up the reveal
 */
const toggleGroupedElements = ($el, options = {}) => {
  const group = $el.data('group');
  const isActive = $el.hasClass('reveal-is-active');
  if (group && !isActive) {
    const $activeGroupContent = $(`.reveal-is-active[data-group='${group}']`);
    $activeGroupContent.each(function activeGroupContent() {
      const $groupedEl = $(this);
      const $content = getOpeningContent($groupedEl);
      const $altContent = getAltOpeningContent($groupedEl);
      if ($groupedEl !== $el) {
        if ($content.is(':visible')) {
          closeContent($content, options);
          openContent($altContent, options);
        } else {
          openContent($content, options);
          closeContent($altContent, options);
        }
        $groupedEl.removeClass('reveal-is-active');
      }
    });
  }
};
/**
 * Toggle Single Element
 * @param {jQuery} $el - button /element that opens reveal
 * @param {jQuery} $content - opening content
 * @param {jQuery} $altContent - alternating opening content
 * @param {Object} options - Options used for wiring up the reveal
 */
const toggleSingleElement = ($el, $content, $altContent, options = {}) => {
  if ($content.is(':visible')) {
    options.onClose($el);
    $content
    .removeClass('reveal-is-active')
    .slideUp(options.duration, () => {
      $el.removeClass('reveal-is-active');
    });
    openContent($altContent, options);
  } else {
    $el.addClass('reveal-is-active');
    openContent($content, options);
    closeContent($altContent, options);
  }
};
/**
 * Handle reveal click event
 * @param {event}  evt - click event
 * @param {jQuery} $el - button /element that opens reveal
 * @param {jQuery} $content - opening content
 * @param {jQuery} $altContent - alternating opening content
 * @param {Object} options - Options used for wiring up the reveal
 */
const revealHandler = (evt, $el, $content, $altContent, options = {}) => {
  const disabledAttr = $el.attr('data-disabled');
  const disabledAttrExist = $el.is('[data-disabled]');
  if (evt) {
    evt.preventDefault();
  }
  if ($el.hasClass('reveal-is-active') && !options.toggle) {
    return;
  }
  if (!disabledAttrExist || disabledAttr === 'false' || disabledAttr === false) {
    options.onOpen($el);
    toggleGroupedElements($el, options);
    toggleSingleElement($el, $content, $altContent, options);
  }
};
/**
 * Set Event Listerners for reveal.
 * @param {jQuery} $el - button /element that opens reveal
 * @param {jQuery} $content - opening content
 * @param {jQuery} $altContent - alternating opening content
 * @param {Object} options - Options used for wiring up the reveal
 */
const setEventListeners = ($el, $content, $altContent, options = {}) => {
  $el.on('click', evt => revealHandler(evt, $el, $content, $altContent, options));
};
/**
 * Enable Reveal - remove disable attributes
 * @param  {jQuery} $el - button /element that opens reveal
 */
const enableReveal = ($el) => {
  $el.attr('data-disabled', 'false')
  .removeClass('disabled')
  .closest('.js-checkout-section-heds').removeClass('disabled');
};
/**
 * Disable Reveal - Add disable attributes
 * @param  {jQuery} $el - button /element that opens reveal
 */
const disableReveal = ($el) => {
  $el.attr('data-disabled', 'true')
  .addClass('disabled')
  .closest('.js-checkout-section-heds').addClass('disabled');
};
/**
 * Add reveala classes to elements
 * @param {jQuery} $el - button /element that opens reveal
 * @param {jQuery} $content - opening content
 */
const addRevealClasses = ($el, $content) => {
  $el.addClass('reveal');
  $content.addClass('reveal__content');
};
/**
 * Initializes reveal.
 * @param {HTMLElement} element - button /element that opens reveal
 * @param {Object} options - Options used for wiring up the reveal
 * @see DEFAULTS
 */
export default function reveal(element, options = {}) {
  const $el = $(element);
  const opts = mergeOptions(DEFAULTS, options, $el);
  const $content = getOpeningContent($el);
  const $altContent = getAltOpeningContent($el);
  addRevealClasses($el, $content);
  setEventListeners($el, $content, $altContent, opts);
  return {
    toggle: () => revealHandler('', $el, $content, $altContent, opts),
    forceToggle: () => toggleSingleElement($el, $content, $altContent, opts),
    enable: () => enableReveal($el),
    disable: () => disableReveal($el),
  };
}
registerJQueryPlugin(PLUGIN_NAME, reveal);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/reveal.js